/**
 * cloud_formation — which HEAP clouds stand in the sky for a weather (2026-10-07, the cloud-realism work; the photo
 * references: docs/research/2026-10-07-cloud-realism/). Pure: no three, no engine values.
 *
 * THE SPLIT. Clouds come in two kinds and the game draws them two ways:
 *   heaps  (cumulus humilis → mediocris → congestus, the scud under a rain deck): real 3D volumes in the world — the
 *          engine's mesh field and its far layer. THIS file picks their formation.
 *   layers (cirrus, cirrocumulus, cirrostratus, altocumulus, altostratus, stratocumulus, stratus, nimbostratus): sheets
 *          in the sky with a layer's perspective (lib/sky/cloud_sheets). A layer drawn as pooled spheres and far sprites
 *          was confetti and stacked plates (the baseline pictures), so with sheets on the mesh field no longer carries
 *          the "altocumulus" / "stratocumulus" / "cirrus" archetypes at all.
 *
 * THE RULE (sheets on), by the weather the game already resolves:
 *   rain / snow     the deck is the sheet's; under it hangs SCUD — ragged fractus, low and sparse, in no direct sun
 *   dry overcast    a stratocumulus deck (the sheet's): no separate heaps
 *   humid           tall cumulus (mediocris) with a few towering congestus among them — and, on the horizon, the
 *                   anvil of a cumulonimbus (far-only: the engine draws it from ~12 km out; a storm you stand under is
 *                   the rain regime's deck)
 *   fair            cumulus, flatter (humilis) the clearer the day
 * With sheets off (the kill switch `atlas:cloud-sheets = 'legacy'` — read by cloud_sheets.cloudSheetsMode — or a tier
 * without the high clouds) the engine's own formation is used unchanged.
 */

export interface HeapLayer { kind: 'cumulus' | 'congestus' | 'cumulonimbus' | 'fractus' | 'stratocumulus' | 'altocumulus' | 'cirrus'; baseAltitude: number; coverage: number; share: number; vigor?: number }
export interface HeapFormation { layers: HeapLayer[] }
export interface HeapSky {
  /** Changes only when the formation does (the field re-streams on a new formation — never per frame). */
  key: string;
  regime: 'fair' | 'humid' | 'wet';
  formation: HeapFormation;
  /** 0..1: the heaps stand UNDER an overcast deck — the direct sun does not reach them (cloud_light dims it). */
  underDeck: number;
}

/** The étages' base altitudes (m): the condensation level of the heaps, and the legacy mid / high mesh layers. */
export const CLOUD_ALTS = { low: 900, mid: 2100, high: 3200 } as const;
/** Scud hangs this far under the condensation level (the rain deck's base). */
export const SCUD_BELOW_M = 320;
/** Dry cover at which the sky is a deck (the sheet's stratocumulus) and separate heaps are gone; the fade starts 0.08 below. */
export const DECK_COVER = 0.82;

const clamp01 = (v: number): number => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));
const smooth = (a: number, b: number, v: number): number => { const t = clamp01((v - a) / (b - a)); return t * t * (3 - 2 * t); };

/** What the layer sheets show (lib/sky/cloud_sheets' SheetSpec, structurally — this file imports nothing). */
export interface HeapSheets { low: { genus: string; cover: number }; mid: { genus: string; cover: number }; high?: { genus: string; cover: number } }

/**
 * The heap clouds for a weather. `sheets` = the layer clouds are drawn as sheets (else `legacy` — the engine's
 * formationForWeather — decides, as before). `sky` = what those sheets show right now: the heaps answer to it —
 *   a low deck (stratocumulus, stratus) closing over them thins them out and takes their sun;
 *   under an altostratus (a warm front: stable air, a watery sun) there are no towers, only a few flat cumulus in shade;
 *   an altocumulus field shades them a little.
 * Without `sky` the cover alone decides (a deck from DECK_COVER). Quantised so the key moves only on a real change.
 */
export function heapSkyFor(
  cover: number, precip: number, sheets: boolean,
  legacy: (cover: number, precip: number, alts: { low: number; mid: number; high: number }) => HeapFormation,
  sky?: HeapSheets | null,
): HeapSky {
  const c = clamp01(cover), p = clamp01(precip);
  const regime: HeapSky['regime'] = p > 0.05 ? 'wet' : c > 0.55 ? 'humid' : 'fair';
  const q = Math.round(c * 10);
  if (!sheets) return { key: `legacy:${regime}:${q}`, regime, formation: legacy(c, p, { ...CLOUD_ALTS }), underDeck: 0 };
  if (regime === 'wet') {
    // SCUD: the field's global coverage is the deck's (≥ 0.9) — the multiplier brings the scud back to a scatter.
    const heavy = p > 0.85 ? 1 : 0;
    return {
      key: `wet:${heavy}`, regime, underDeck: 1,
      formation: { layers: [{ kind: 'fractus', baseAltitude: CLOUD_ALTS.low - SCUD_BELOW_M, coverage: heavy ? 0.5 : 0.42, share: 1, vigor: 0.3 }] },
    };
  }
  // The decks over the heaps. With the sheets' state: the low sheet's cover L (a layer deck — never the rain's, handled
  // above), the altostratus' M, an altocumulus field's A. A stratocumulus day's air is CAPPED: the sheet's pads are the
  // low clouds (from below they read as flat-based cumulus themselves — two populations at one level is a busy, wrong
  // sky), so the heaps thin out early (gone by L 0.7) and no tower stands under it. Without the sheets' state the cover
  // alone closes the sky (a deck from DECK_COVER).
  let fade: number, stable = 0, capped = 0, underDeck: number;
  if (sky) {
    const L = /^(sc|st)/.test(sky.low.genus) ? clamp01(sky.low.cover) : 0;
    const M = sky.mid.genus === 'as' ? clamp01(sky.mid.cover) : 0;
    const A = /^ac/.test(sky.mid.genus) ? clamp01(sky.mid.cover) : 0;
    fade = 1 - smooth(0.3, 0.7, L);
    capped = smooth(0.25, 0.55, L);
    stable = smooth(0.5, 0.9, M);                         // a warm front's air: no towers
    underDeck = clamp01(Math.max(smooth(0.4, 0.85, L), 0.9 * smooth(0.3, 0.9, M), 0.25 * A));
  } else {
    fade = c > 0.55 ? 1 - smooth(DECK_COVER - 0.08, DECK_COVER, c) : 1;
    underDeck = 1 - fade;
  }
  if (Math.round(fade * 5) === 0) {
    // A deck: no heaps (a layer at coverage 0 spawns nothing; the field needs one layer to hold its formation).
    return { key: 'deck', regime, underDeck: 1, formation: { layers: [{ kind: 'cumulus', baseAltitude: CLOUD_ALTS.low, coverage: 0, share: 1, vigor: 0.2 }] } };
  }
  const qf = Math.round(fade * 5), qs = Math.round(stable * 4), qc = Math.round(capped * 4);
  const f = qf / 5, st = qs / 4, cap = qc / 4; // the formation is built from the QUANTISED values: one key, one formation
  if (regime === 'humid') {
    if (st >= 0.75) {
      // under the altostratus: a few flat cumulus, no towers
      return { key: `stable:${q}:${qf}`, regime, underDeck, formation: { layers: [{ kind: 'cumulus', baseAltitude: CLOUD_ALTS.low, coverage: 0.6 * f, share: 1, vigor: 0.25 }] } };
    }
    const tower = (1 - st) * (1 - cap);
    return {
      key: `humid:${q}:${qf}:${qs}:${qc}`, regime, underDeck,
      formation: { layers: [
        { kind: 'cumulus', baseAltitude: CLOUD_ALTS.low, coverage: 0.95 * f, share: 0.8, vigor: +(0.75 - 0.4 * st).toFixed(2) },
        { kind: 'congestus', baseAltitude: CLOUD_ALTS.low, coverage: 0.6 * f * tower, share: 0.16, vigor: 0.8 },
        { kind: 'cumulonimbus', baseAltitude: CLOUD_ALTS.low, coverage: 0.62 * f * tower, share: 0.04, vigor: 0.9 },
      ] },
    };
  }
  // FAIR: flatter the clearer the day (humilis at cover 0.1, mediocris toward 0.55).
  const vigor = 0.2 + 0.45 * clamp01(c / 0.55);
  return {
    key: `fair:${q}:${qf}`, regime, underDeck,
    formation: { layers: [{ kind: 'cumulus', baseAltitude: CLOUD_ALTS.low, coverage: f, share: 1, vigor: +vigor.toFixed(2) }] },
  };
}
